import AiOperationalPolicy from "./AiOperationalPolicy";
import AiOperationalFailureSettings from "./AiOperationalFailureSettings";
import { getConfigurationMode, getSaveFeedback } from "./userConfiguration.mjs";
import { buildUserPasswordSchema } from "./passwordPolicy.mjs";
import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Avatar,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	Typography,
	Tooltip,
	FormControlLabel,
	Switch,
	Checkbox,
	Accordion,
	AccordionSummary,
	AccordionDetails
  } from '@material-ui/core';

import {
	AddCircleOutline,
	DeleteOutline,
	ExpandMore,
	Memory,
	PersonOutline,
	SwapHoriz,
	Visibility,
	VisibilityOff,
} from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import ButtonWithSpinner from "../ButtonWithSpinner";
import AiAgentWizard from "./AiAgentWizard";
import AiOperationalSkills, {
	AI_OPERATIONAL_SKILL_NAMES,
} from "./AiOperationalSkills";
import HumanUserWizard from "./HumanUserWizard";
import {
	captureAiAdvancedModeDraft,
	getAiCreationReadiness,
	getCreationSectionOrder,
	getHumanCreationReadiness,
	normalizeAiAgentMetadata,
	switchAiAgentSetupMode,
	shouldShowCreationTypeChoice,
	shouldUseSimplifiedCreationWizard,
} from "./userCreationFlow.mjs";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		width: "min(1040px, calc(100vw - 48px))",
		maxHeight: "calc(100vh - 64px)",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		[theme.breakpoints.down("xs")]: {
			width: "calc(100vw - 16px)",
			margin: theme.spacing(1),
		},
	},
	dialogContent: {
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		overflowY: "auto",
		scrollBehavior: "smooth",
		padding: theme.spacing(2.5, 3),
		"& .MuiFormControl-marginDense": {
			marginTop: theme.spacing(1.5),
			marginBottom: theme.spacing(1.5),
		},
		"& > *:not(:first-child)": {
			marginTop: theme.spacing(2.5),
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(2),
		},
	},
	formRoot: {
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		flex: 1,
	},
	dialogActions: {
		flexShrink: 0,
		padding: theme.spacing(1.5, 3),
		borderTop: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1.25, 2),
			flexWrap: "wrap",
		},
	},
	actionsSpacer: { flex: 1 },
	wizardFrame: {
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		order: 1,
	},
	multFieldLine: {
		display: "block",
		"& > *": {
			display: "block",
			width: "100%",
		},
		"& > *:not(:first-child)": {
			marginTop: theme.spacing(3),
		},
		"& + $multFieldLine": {
			marginTop: theme.spacing(3),
		},
	},

	actionButton: {
		borderRadius: 999,
		boxShadow: "none",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
	formControl: {
		margin: 0,
		width: "100%",
		minWidth: 0,
	},
	avatarRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
		order: 1,
	},
	creationChoice: {
		padding: theme.spacing(1, 0, 2),
	},
	creationChoiceTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.75),
	},
	creationChoiceHelp: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(3),
	},
	creationChoiceGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	creationChoiceCard: {
		minHeight: 156,
		padding: theme.spacing(2.5),
		borderRadius: 16,
		borderColor: theme.palette.divider,
		alignItems: "flex-start",
		justifyContent: "flex-start",
		textAlign: "left",
		textTransform: "none",
		boxShadow: "none",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow: "none",
		},
	},
	creationChoiceCardContent: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		width: "100%",
	},
	creationChoiceIcon: {
		width: 44,
		height: 44,
		borderRadius: 12,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.action.hover,
		color: theme.palette.primary.main,
		flexShrink: 0,
	},
	creationChoiceCardTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.75),
	},
	creationChoiceCardHelp: {
		color: theme.palette.text.secondary,
		lineHeight: 1.55,
	},
	creationTypeSummary: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		padding: theme.spacing(1.5, 2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		order: 0,
		[theme.breakpoints.down("xs")]: {
			alignItems: "flex-start",
			flexDirection: "column",
		},
	},
	creationTypeSummaryLabel: {
		fontWeight: 700,
	},
	creationTypeSummaryHelp: {
		color: theme.palette.text.secondary,
	},
	avatarPreview: {
		width: 56,
		height: 56,
		fontWeight: 700,
	},
	formSection: {
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		"& + $formSection": {
			marginTop: theme.spacing(2.5),
		},
	},
	formSectionTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5),
	},
	formSectionHelp: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(2),
	},
	hiddenInput: {
		display: "none",
	},
	aiSection: {
		"& > .MuiFormControl-root": {
			marginTop: theme.spacing(1.5),
			marginBottom: theme.spacing(1.5),
		},
		"& > .MuiFormControlLabel-root": {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
		},
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(0.75),
	},
	sectionHelp: {
		marginBottom: theme.spacing(1.5),
		color: theme.palette.text.secondary,
	},
	compactGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
		columnGap: theme.spacing(2.5),
		rowGap: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	switchGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
		columnGap: theme.spacing(2),
		rowGap: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	promptAccordion: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
		"&$expanded": {
			marginTop: theme.spacing(1.5),
		},
	},
	expanded: {},
	promptSummary: {
		minHeight: 48,
		"&$expanded": {
			minHeight: 48,
		},
		"& .MuiAccordionSummary-content": {
			margin: theme.spacing(1, 0),
			display: "block",
		},
		"& .MuiAccordionSummary-content.Mui-expanded": {
			margin: theme.spacing(1, 0),
		},
	},
	promptDetails: {
		display: "block",
		padding: theme.spacing(0, 2, 2),
	},
	promptSummaryText: {
		color: theme.palette.text.secondary,
		display: "block",
		marginTop: theme.spacing(0.25),
	},
	capabilitiesHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	capabilityItem: {
		paddingTop: theme.spacing(1),
		marginTop: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	capabilityTitleRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
	},
}));

const buildUserSchema = (isEditing = false) => Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("userModal.validation.tooShort"))
		.max(50, i18n.t("userModal.validation.tooLong"))
		.required(i18n.t("userModal.validation.required")),
	userType: Yup.string().oneOf(["human", "ai_agent"]).required(i18n.t("userModal.validation.required")),
	password: buildUserPasswordSchema({ isEditing, t: key => i18n.t(key) }),
	email: Yup.string().email(i18n.t("userModal.validation.invalidEmail")).when("userType", {
		is: "ai_agent",
		then: Yup.string().notRequired(),
		otherwise: Yup.string().required(i18n.t("userModal.validation.required"))
	}),
	language: Yup.string().oneOf(["pt-BR", "en", "es"]).required(i18n.t("userModal.validation.required")),
	ticketOrder: Yup.string().oneOf(["oldest_first", "newest_first"]).required(i18n.t("userModal.validation.required")),
	aiSignMessages: Yup.boolean(),
	aiAgentProfile: Yup.object().when("userType", {
		is: "ai_agent",
		then: Yup.object().shape({
			instructions: Yup.string().when("metadata", {
				is: metadata => metadata?.setupMode === "guided",
				then: Yup.string().notRequired(),
				otherwise: Yup.string().required(i18n.t("userModal.validation.required")),
			}),
			llmProvider: Yup.string().required(i18n.t("userModal.validation.required")),
			llmModel: Yup.string().required(i18n.t("userModal.validation.required")),
			temperature: Yup.number().min(0).max(2).nullable(),
			maxTokens: Yup.number().integer().positive().nullable(),
			responseDelaySeconds: Yup.number().integer().min(0).max(120).nullable(),
			proactiveDelayMinutes: Yup.number().integer().min(0).nullable(),
			maxProactiveMessages: Yup.number().integer().min(0).nullable(),
			proactivePrompt: Yup.string().nullable(),
			unavailableMessage: Yup.string().nullable(),
			proactiveClosureKeywords: Yup.string().nullable(),
			proactiveClosurePrompt: Yup.string().nullable(),
			proactiveCloseSilently: Yup.boolean(),
			allowDirectTicketClosure: Yup.boolean(),
			enabledInternalSkillNames: Yup.array().of(
				Yup.string().oneOf(AI_OPERATIONAL_SKILL_NAMES)
			),
			allowAudioMessages: Yup.boolean(),
			allowImageMessages: Yup.boolean(),
			allowDocumentMessages: Yup.boolean(),
			maxMediaFileSizeMb: Yup.number().integer().min(1).max(100).nullable(),
			loopHandoffQueueId: Yup.number()
				.transform((value, originalValue) => (originalValue === "" ? null : value))
				.integer()
				.positive()
				.nullable(),
			dissatisfactionEscalationEnabled: Yup.boolean(),
			dissatisfactionEscalationQueueId: Yup.number()
				.transform((value, originalValue) => (originalValue === "" ? null : value))
				.integer()
				.positive()
				.nullable()
				.when("dissatisfactionEscalationEnabled", {
					is: true,
					then: Yup.number().required(i18n.t("userModal.validation.required")),
				}),
			capabilities: Yup.array().of(
				Yup.object().shape({
					name: Yup.string().required(i18n.t("userModal.validation.required")),
					description: Yup.string().required(i18n.t("userModal.validation.required")),
					type: Yup.string().required(i18n.t("userModal.validation.required")),
					instructions: Yup.string().required(i18n.t("userModal.validation.required")),
					enabled: Yup.boolean()
				})
			)
		})
	}),
	accessSchedule: Yup.object().shape({
		enabled: Yup.boolean(),
		profileId: Yup.mixed().nullable(),
		saveAsProfileName: Yup.string().nullable(),
		timezone: Yup.string().nullable(),
		daysOfWeek: Yup.array().of(Yup.number()),
		startTime: Yup.string().nullable(),
		endTime: Yup.string().nullable(),
		warnBeforeMinutes: Yup.number().integer().min(0).nullable(),
		forceLogoutHumans: Yup.boolean(),
		pauseAiOutsideHours: Yup.boolean(),
		blockHumanActionsOutsideHours: Yup.boolean(),
		temporaryOverrideUntil: Yup.string().nullable(),
		temporaryOverrideReason: Yup.string().nullable(),
	})
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		userType: "human",
		language: "pt-BR",
		ticketOrder: "oldest_first",
		aiSignMessages: true,
		profilePicUrl: "",
		aiAgentProfile: {
			name: "",
			description: "",
			instructions: "",
			llmProvider: "openai",
			llmModel: "gpt-4o-mini",
			temperature: 0.7,
			maxTokens: "",
			responseDelaySeconds: 8,
			proactiveDelayMinutes: 0,
			maxProactiveMessages: 0,
			proactivePrompt: "",
			unavailableMessage: "",
			proactiveClosureKeywords: "",
			proactiveClosurePrompt: "",
			proactiveCloseSilently: true,
			allowDirectTicketClosure: false,
			enabledInternalSkillNames: ["talk_handoff"],
			allowAudioMessages: false,
			allowImageMessages: false,
			allowDocumentMessages: false,
			maxMediaFileSizeMb: 10,
			loopHandoffQueueId: "",
			dissatisfactionEscalationEnabled: false,
			dissatisfactionEscalationQueueId: "",
			capabilities: [],
			metadata: {
				operationalHandoffEnabled: true,
				setupMode: "guided",
				templateKey: "medical_clinic",
				templateVersion: 1,
				intelligenceProfile: "balanced",
				tone: "welcoming",
				responseLength: "balanced",
				autonomyLevel: "confirm_actions",
				selectedCapabilityKeys: [],
				capabilitiesInitialized: false,
				businessData: {
					organizationName: "",
					summary: "",
					services: "",
					address: "",
					businessHours: "",
					schedulingUrl: "",
					advancedOperations: "",
					additionalRules: "",
				},
				wizardCompleted: false,
			},
		},
		accessSchedule: {
			enabled: false,
			profileId: "",
			saveAsProfileName: "",
			timezone: "America/Sao_Paulo",
			daysOfWeek: [1, 2, 3, 4, 5],
			startTime: "08:00",
			endTime: "18:00",
			warnBeforeMinutes: 30,
			forceLogoutHumans: true,
			pauseAiOutsideHours: true,
			blockHumanActionsOutsideHours: true,
			temporaryOverrideUntil: "",
			temporaryOverrideReason: "",
		},
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState(false);
	const [profilePicFile, setProfilePicFile] = useState(null);
	const [profilePicPreview, setProfilePicPreview] = useState("");
	const [queues, setQueues] = useState([]);
	const [scheduleProfiles, setScheduleProfiles] = useState([]);
	const [savingUser, setSavingUser] = useState(false);
	const [creationTypeConfirmed, setCreationTypeConfirmed] = useState(Boolean(userId));
	const [creationStep, setCreationStep] = useState(0);
	const savingUserRef = useRef(false);
	const deploymentActionRef = useRef("publish");
	const advancedModeDraftRef = useRef(null);
	const {loading, whatsApps} = useWhatsApps();

	useEffect(() => {
		if (!open) return;
		setCreationTypeConfirmed(Boolean(userId));
		setCreationStep(0);
		advancedModeDraftRef.current = null;

		const fetchQueues = async () => {
			try {
				const [{ data }, profilesResponse] = await Promise.all([
					api.get("/queue"),
					api.get("/user-access-schedules/profiles"),
				]);
				setQueues(Array.isArray(data) ? data : []);
				setScheduleProfiles(Array.isArray(profilesResponse.data) ? profilesResponse.data : []);
			} catch (err) {
				toastError(err);
				setQueues([]);
				setScheduleProfiles([]);
			}
		};

		fetchQueues();
	}, [open]);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setShowPassword(false);
				const normalizedMetadata = normalizeAiAgentMetadata(
					data.aiAgentProfile?.metadata
				);
				setUser(prevState => {
					return {
						...prevState,
						...data,
						password: "",
						aiAgentProfile: {
							...prevState.aiAgentProfile,
							...(data.aiAgentProfile || {}),
							metadata: {
								...prevState.aiAgentProfile.metadata,
								...normalizedMetadata,
								setupMode:
									normalizedMetadata.setupMode ||
									(data.aiAgentProfile ? "advanced" : "guided"),
								businessData: {
									...prevState.aiAgentProfile.metadata.businessData,
									...(normalizedMetadata.businessData || {}),
								},
							},
							loopHandoffQueueId:
								data.aiAgentProfile?.loopHandoffQueueId || "",
							enabledInternalSkillNames: Array.isArray(
								data.aiAgentProfile?.enabledInternalSkillNames
							)
								? data.aiAgentProfile.enabledInternalSkillNames
								: [...AI_OPERATIONAL_SKILL_NAMES],
							dissatisfactionEscalationQueueId:
								data.aiAgentProfile?.dissatisfactionEscalationQueueId || "",
							capabilities: Array.isArray(data.aiAgentProfile?.capabilities)
								? data.aiAgentProfile.capabilities
								: prevState.aiAgentProfile.capabilities,
						},
						accessSchedule: {
							...prevState.accessSchedule,
							...(data.accessSchedule || {}),
							profileId: data.accessSchedule?.profileId || "",
							daysOfWeek: Array.isArray(data.accessSchedule?.daysOfWeek)
								? data.accessSchedule.daysOfWeek
								: prevState.accessSchedule.daysOfWeek,
							temporaryOverrideUntil: "",
							temporaryOverrideReason: "",
						},
					};
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
				setProfilePicPreview(data.profilePicUrl || "");
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		if (savingUserRef.current) {
			return;
		}

		onClose();
		setUser(initialState);
		setProfilePicFile(null);
		setProfilePicPreview("");
		setSelectedQueueIds([]);
		setWhatsappId("");
		setCreationTypeConfirmed(Boolean(userId));
		setCreationStep(0);
		advancedModeDraftRef.current = null;
	};

	const handleSaveUser = async values => {
		if (savingUserRef.current) {
			return;
		}

		savingUserRef.current = true;
		setSavingUser(true);

		const { profilePicUrl, ...editableValues } = values;
		if (userId && editableValues.password === "") delete editableValues.password;
		const accessSchedule = editableValues.accessSchedule
			? {
				...editableValues.accessSchedule,
				profileId: editableValues.accessSchedule.profileId || null,
				temporaryOverrideUntil: editableValues.accessSchedule.temporaryOverrideUntil
					? new Date(editableValues.accessSchedule.temporaryOverrideUntil).toISOString()
					: null,
			}
			: null;
		const userData = {
			...editableValues,
			accessSchedule,
			aiAgentProfile:
				editableValues.userType === "ai_agent"
					? {
						...editableValues.aiAgentProfile,
						metadata: normalizeAiAgentMetadata(
							editableValues.aiAgentProfile?.metadata
						),
						name: editableValues.aiAgentProfile?.name || editableValues.name,
						deploymentAction:
							editableValues.aiAgentProfile?.metadata?.setupMode === "guided"
								? deploymentActionRef.current
								: "sync",
					}
					: null,
			whatsappId,
			queueIds: selectedQueueIds
		};
		const payload = profilePicFile ? new FormData() : userData;

		if (profilePicFile) {
			Object.entries(userData).forEach(([key, value]) => {
				if (key === "queueIds") {
					payload.append(key, JSON.stringify(value || []));
				} else if (key === "aiAgentProfile") {
					payload.append(key, JSON.stringify(value || null));
				} else if (key === "accessSchedule") {
					payload.append(key, JSON.stringify(value || null));
				} else if (value !== undefined && value !== null) {
					payload.append(key, value);
				}
			});
			payload.append("profilePic", profilePicFile);
		}

		try {
			const response = userId ? await api.put(`/users/${userId}`, payload) : await api.post("/users", payload);
			const feedback = getSaveFeedback(editableValues.userType, response.data?.aiAgentProfile, deploymentActionRef.current);
			const notify = feedback === "syncPending" ? toast.warning : toast.success;
			notify(i18n.t("userModal.configuration." + feedback));
			onClose();
			setUser(initialState);
			setProfilePicFile(null);
			setProfilePicPreview("");
			setSelectedQueueIds([]);
			setWhatsappId("");
		} catch (err) {
			toastError(err);
		} finally {
			deploymentActionRef.current = "publish";
			savingUserRef.current = false;
			setSavingUser(false);
		}
	};

	const getInitials = name => String(name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part[0])
		.join("")
		.toUpperCase();

	const handleProfilePicChange = event => {
		const file = event.target.files?.[0];
		if (!file) return;

		setProfilePicFile(file);
		setProfilePicPreview(URL.createObjectURL(file));
	};

	const weekdayOptions = [
		{ value: 0, label: i18n.t("userModal.accessSchedule.weekdays.sun") },
		{ value: 1, label: i18n.t("userModal.accessSchedule.weekdays.mon") },
		{ value: 2, label: i18n.t("userModal.accessSchedule.weekdays.tue") },
		{ value: 3, label: i18n.t("userModal.accessSchedule.weekdays.wed") },
		{ value: 4, label: i18n.t("userModal.accessSchedule.weekdays.thu") },
		{ value: 5, label: i18n.t("userModal.accessSchedule.weekdays.fri") },
		{ value: 6, label: i18n.t("userModal.accessSchedule.weekdays.sat") },
	];

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
				classes={{ paper: classes.dialogPaper }}
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={buildUserSchema(Boolean(userId))}
					onSubmit={async (values, actions) => {
						try {
							await handleSaveUser(values);
						} finally {
							actions.setSubmitting(false);
						}
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue, setFieldTouched }) => {
						const isSaving = isSubmitting || savingUser;
						const showCreationTypeChoice = shouldShowCreationTypeChoice({
							userId,
							creationTypeConfirmed,
						});
						const sectionOrder = getCreationSectionOrder(values.userType);
						const useSimplifiedWizard = shouldUseSimplifiedCreationWizard({
							userId,
							creationTypeConfirmed,
							userType: values.userType,
							setupMode: values.aiAgentProfile?.metadata?.setupMode,
						});
						const isHumanWizard = useSimplifiedWizard && values.userType === "human";
						const isAiWizard = useSimplifiedWizard && values.userType === "ai_agent";
						const humanReadiness = getHumanCreationReadiness(values);
						const aiReadiness = getAiCreationReadiness({
							name: values.name,
							templateKey: values.aiAgentProfile?.metadata?.templateKey,
							selectedCapabilityKeys: values.aiAgentProfile?.metadata?.selectedCapabilityKeys,
							businessData: values.aiAgentProfile?.metadata?.businessData,
							whatsappId,
							selectedQueueIds,
							autonomyLevel: values.aiAgentProfile?.metadata?.autonomyLevel,
						});
						const wizardStepReady = isHumanWizard
							? humanReadiness.stepReady[creationStep]
							: aiReadiness.stepReady[creationStep];
						const wizardLastStep = isHumanWizard ? 2 : 4;
						const isWizardFinalStep = creationStep === wizardLastStep;
						const changeAiSetupMode = targetMode => {
							const currentMode = values.aiAgentProfile?.metadata?.setupMode === "advanced"
								? "advanced"
								: "guided";
							if (
								currentMode === "advanced" &&
								targetMode === "guided" &&
								!window.confirm(i18n.t("userModal.aiAgent.wizard.modeSwitchToGuidedConfirm"))
							) return;
							if (currentMode === "advanced" && targetMode === "guided") {
								advancedModeDraftRef.current = captureAiAdvancedModeDraft(values.aiAgentProfile);
							}
							const nextProfile = switchAiAgentSetupMode(
								values.aiAgentProfile,
								targetMode,
								advancedModeDraftRef.current
							);
							setFieldValue("aiAgentProfile", nextProfile, false);
							setCreationStep(0);
							toast.info(i18n.t("userModal.aiAgent.wizard.modeDraftPreserved"));
						};
						const selectCreationType = userType => {
							setFieldValue("userType", userType);
							if (userType === "ai_agent") {
								setFieldValue("aiAgentProfile.metadata.setupMode", "guided");
							}
							setCreationStep(0);
							setCreationTypeConfirmed(true);
						};
						const goToNextCreationStep = () => {
							if (!wizardStepReady) {
								if (isHumanWizard && creationStep === 0) {
									["name", "email", "password"].forEach(field => setFieldTouched(field, true, false));
								}
								if (isAiWizard && creationStep === 0) setFieldTouched("name", true, false);
								if (isAiWizard && creationStep === 1) {
									setFieldTouched("aiAgentProfile.metadata.businessData.organizationName", true, false);
								}
								return;
							}
							const nextStep = Math.min(creationStep + 1, wizardLastStep);
							if (isAiWizard && nextStep === wizardLastStep) {
								setFieldValue("aiAgentProfile.metadata.wizardCompleted", true, false);
							}
							setCreationStep(nextStep);
						};

						return (
							<Form className={classes.formRoot}>
							<DialogContent dividers className={classes.dialogContent}>
								{showCreationTypeChoice ? (
									<div className={classes.creationChoice} data-testid="user-creation-type-choice">
										<Typography variant="h6" className={classes.creationChoiceTitle}>
											{i18n.t("userModal.creationChoice.title")}
										</Typography>
										<Typography variant="body2" className={classes.creationChoiceHelp}>
											{i18n.t("userModal.creationChoice.help")}
										</Typography>
										<div className={classes.creationChoiceGrid}>
											<Button
												type="button"
												variant="outlined"
												className={classes.creationChoiceCard}
												onClick={() => selectCreationType("human")}
												data-testid="create-human-user"
											>
												<div className={classes.creationChoiceCardContent}>
													<div className={classes.creationChoiceIcon}><PersonOutline /></div>
													<div>
														<Typography className={classes.creationChoiceCardTitle}>
															{i18n.t("userModal.creationChoice.humanTitle")}
														</Typography>
														<Typography variant="body2" className={classes.creationChoiceCardHelp}>
															{i18n.t("userModal.creationChoice.humanDescription")}
														</Typography>
													</div>
												</div>
											</Button>
											<Button
												type="button"
												variant="outlined"
												color="primary"
												className={classes.creationChoiceCard}
												onClick={() => selectCreationType("ai_agent")}
												data-testid="create-ai-agent"
											>
												<div className={classes.creationChoiceCardContent}>
													<div className={classes.creationChoiceIcon}><Memory /></div>
													<div>
														<Typography className={classes.creationChoiceCardTitle}>
															{i18n.t("userModal.creationChoice.aiTitle")}
														</Typography>
														<Typography variant="body2" className={classes.creationChoiceCardHelp}>
															{i18n.t("userModal.creationChoice.aiDescription")}
														</Typography>
													</div>
												</div>
											</Button>
										</div>
									</div>
								) : (
								<>
								{!userId && (
									<div className={classes.creationTypeSummary}>
										<div>
											<Typography className={classes.creationTypeSummaryLabel}>
												{values.userType === "ai_agent"
													? i18n.t("userModal.creationChoice.aiSelected")
													: i18n.t("userModal.creationChoice.humanSelected")}
											</Typography>
											<Typography variant="body2" className={classes.creationTypeSummaryHelp}>
												{values.userType === "ai_agent"
													? i18n.t("userModal.creationChoice.aiSelectedHelp")
													: i18n.t("userModal.creationChoice.humanSelectedHelp")}
											</Typography>
										</div>
										<Button
											type="button"
											variant="outlined"
											className={classes.actionButton}
											startIcon={<SwapHoriz />}
											onClick={() => setCreationTypeConfirmed(false)}
										>
											{i18n.t("userModal.creationChoice.changeType")}
										</Button>
									</div>
								)}
								{isHumanWizard && (
									<div className={classes.wizardFrame}>
										<HumanUserWizard
											values={values}
											setFieldValue={setFieldValue}
											setFieldTouched={setFieldTouched}
											touched={touched}
											errors={errors}
											activeStep={creationStep}
											selectedQueueIds={selectedQueueIds}
											onQueueIdsChange={setSelectedQueueIds}
											whatsappId={whatsappId}
											onWhatsappIdChange={setWhatsappId}
											whatsApps={whatsApps}
											loadingWhatsApps={loading}
											showPassword={showPassword}
											onTogglePassword={() => setShowPassword(value => !value)}
										/>
									</div>
								)}
								{isAiWizard && (
									<div className={classes.wizardFrame}>
										<AiAgentWizard
											values={values}
											setFieldValue={setFieldValue}
											touched={touched}
											errors={errors}
											queues={queues}
											activeStep={creationStep}
											selectedQueueIds={selectedQueueIds}
											onQueueIdsChange={setSelectedQueueIds}
											whatsappId={whatsappId}
											onWhatsappIdChange={setWhatsappId}
											whatsApps={whatsApps}
											loadingWhatsApps={loading}
											readiness={aiReadiness}
											userId={userId}
											onSwitchSetupMode={changeAiSetupMode}
										/>
									</div>
								)}
								{isAiWizard && <AiOperationalPolicy values={values} setFieldValue={setFieldValue} userId={userId} />}
                                {!useSimplifiedWizard && (
								<>
								<div className={classes.avatarRow}>
									<Avatar
										className={classes.avatarPreview}
										src={profilePicPreview || user.profilePicUrl}
									>
										{getInitials(user.name)}
									</Avatar>
									<div>
										<input
											accept="image/*"
											className={classes.hiddenInput}
											id="user-profile-pic-input"
											type="file"
											onChange={handleProfilePicChange}
										/>
										<label htmlFor="user-profile-pic-input">
											<Button
												component="span"
												variant="outlined"
												className={classes.actionButton}
												disabled={isSaving}
											>
												{i18n.t("userModal.form.profilePic")}
											</Button>
										</label>
									</div>
								</div>
								<div className={classes.formSection} style={{ order: sectionOrder.identity }}>
									<Typography variant="subtitle1" className={classes.formSectionTitle}>
										{i18n.t("userModal.sections.userData")}
									</Typography>
									<Typography variant="body2" className={classes.formSectionHelp}>
										{i18n.t("userModal.sections.userDataHelp")}
									</Typography>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									{values.userType !== "ai_agent" && (
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t(userId ? "myProfile.password" : "userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={(touched.password && errors.password) || (userId ? i18n.t("myProfile.passwordHelp") : "")}
										autoComplete="new-password"
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
									)}
								</div>
								<div className={classes.multFieldLine}>
									{values.userType !== "ai_agent" && (
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									)}
									{values.userType !== "ai_agent" && (
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="user">User</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
									)}
									{userId && (
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<InputLabel id="user-type-selection-input-label">
											{i18n.t("userModal.form.userType")}
										</InputLabel>

										<Field
											as={Select}
											label={i18n.t("userModal.form.userType")}
											name="userType"
											labelId="user-type-selection-label"
											id="user-type-selection"
											required
										>
											<MenuItem value="human">
												{i18n.t("userModal.form.userTypeOptions.human")}
											</MenuItem>
											<MenuItem value="ai_agent">
												{i18n.t("userModal.form.userTypeOptions.aiAgent")}
											</MenuItem>
										</Field>
									</FormControl>
									)}
									<div className={classes.compactGrid}>
										<FormControl
											variant="outlined"
											className={classes.formControl}
											margin="dense"
										>
											<InputLabel id="language-selection-input-label">
												{i18n.t("userModal.form.language")}
											</InputLabel>

											<Field
												as={Select}
												label={i18n.t("userModal.form.language")}
												name="language"
												labelId="language-selection-label"
												id="language-selection"
												required
											>
												<MenuItem value="pt-BR">{i18n.t("signup.form.languageOptions.pt")}</MenuItem>
												<MenuItem value="en">{i18n.t("signup.form.languageOptions.en")}</MenuItem>
												<MenuItem value="es">{i18n.t("signup.form.languageOptions.es")}</MenuItem>
											</Field>
										</FormControl>
										<FormControl
											variant="outlined"
											className={classes.formControl}
											margin="dense"
										>
											<InputLabel id="ticket-order-selection-input-label">
												{i18n.t("userModal.form.ticketOrder")}
											</InputLabel>

											<Field
												as={Select}
												label={i18n.t("userModal.form.ticketOrder")}
												name="ticketOrder"
												labelId="ticket-order-selection-label"
												id="ticket-order-selection"
												required
											>
												<MenuItem value="oldest_first">
													{i18n.t("userModal.form.ticketOrderOptions.oldestFirst")}
												</MenuItem>
												<MenuItem value="newest_first">
													{i18n.t("userModal.form.ticketOrderOptions.newestFirst")}
												</MenuItem>
											</Field>
										</FormControl>
									</div>
								</div>
								</div>
								<div className={classes.formSection} style={{ order: sectionOrder.attendance }}>
									<Typography variant="subtitle1" className={classes.formSectionTitle}>
										{i18n.t("userModal.sections.attendance")}
									</Typography>
									<Typography variant="body2" className={classes.formSectionHelp}>
										{i18n.t("userModal.sections.attendanceHelp")}
									</Typography>
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (
											<QueueSelect
												selectedQueueIds={selectedQueueIds}
												onChange={values => setSelectedQueueIds(values)}
											/>
										)}
									/>
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (!loading &&
											<FormControl variant="outlined" margin="dense" className={classes.formControl} fullWidth>
												<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
												<Field
													as={Select}
													value={whatsappId}
													onChange={(e) => setWhatsappId(e.target.value)}
													label={i18n.t("userModal.form.whatsapp")}
												>
													<MenuItem value={''}>&nbsp;</MenuItem>
													{whatsApps.map((whatsapp) => (
														<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
													))}
												</Field>
											</FormControl>
										)}
									/>
								</div>
								<div className={classes.formSection} style={{ order: sectionOrder.accessSchedule }}>
									<div className={classes.aiSection}>
										<Typography variant="subtitle2" className={classes.sectionTitle}>
											{i18n.t("userModal.accessSchedule.title")}
										</Typography>
										<Typography variant="body2" className={classes.sectionHelp}>
											{i18n.t("userModal.accessSchedule.help")}
										</Typography>
										<FormControlLabel
											control={
												<Switch
													color="primary"
													checked={values.accessSchedule?.enabled === true}
													onChange={event =>
														setFieldValue("accessSchedule.enabled", event.target.checked)
													}
												/>
											}
											label={i18n.t("userModal.accessSchedule.enabled")}
										/>
										{values.accessSchedule?.enabled === true && (
											<>
										<FormControl
											variant="outlined"
											className={classes.formControl}
											margin="dense"
										>
											<InputLabel id="access-schedule-profile-label">
												{i18n.t("userModal.accessSchedule.profile")}
											</InputLabel>
											<Field
												as={Select}
												label={i18n.t("userModal.accessSchedule.profile")}
												name="accessSchedule.profileId"
												labelId="access-schedule-profile-label"
												id="access-schedule-profile"
											>
												<MenuItem value="">
													{i18n.t("userModal.accessSchedule.customProfile")}
												</MenuItem>
												{scheduleProfiles.map(profile => (
													<MenuItem key={profile.id} value={profile.id}>
														{profile.name}
													</MenuItem>
												))}
											</Field>
										</FormControl>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.saveAsProfileName")}
											name="accessSchedule.saveAsProfileName"
											variant="outlined"
											margin="dense"
											helperText={i18n.t("userModal.accessSchedule.saveAsProfileNameHelp")}
											fullWidth
										/>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.timezone")}
											name="accessSchedule.timezone"
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<FormControl
											variant="outlined"
											className={classes.formControl}
											margin="dense"
										>
											<InputLabel id="access-schedule-days-label">
												{i18n.t("userModal.accessSchedule.daysOfWeek")}
											</InputLabel>
											<Select
												multiple
												label={i18n.t("userModal.accessSchedule.daysOfWeek")}
												labelId="access-schedule-days-label"
												value={values.accessSchedule?.daysOfWeek || []}
												onChange={event =>
													setFieldValue(
														"accessSchedule.daysOfWeek",
														event.target.value.map(Number)
													)
												}
												renderValue={selected =>
													weekdayOptions
														.filter(option => selected.includes(option.value))
														.map(option => option.label)
														.join(", ")
												}
											>
												{weekdayOptions.map(option => (
													<MenuItem key={option.value} value={option.value}>
														<Checkbox
															checked={(values.accessSchedule?.daysOfWeek || []).includes(option.value)}
															color="primary"
														/>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.startTime")}
											name="accessSchedule.startTime"
											type="time"
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.endTime")}
											name="accessSchedule.endTime"
											type="time"
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.warnBeforeMinutes")}
											name="accessSchedule.warnBeforeMinutes"
											type="number"
											inputProps={{ min: 0, step: 1 }}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										{values.userType !== "ai_agent" && (
											<>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={values.accessSchedule?.forceLogoutHumans !== false}
															onChange={event =>
																setFieldValue("accessSchedule.forceLogoutHumans", event.target.checked)
															}
														/>
													}
													label={i18n.t("userModal.accessSchedule.forceLogoutHumans")}
												/>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={values.accessSchedule?.blockHumanActionsOutsideHours !== false}
															onChange={event =>
																setFieldValue(
																	"accessSchedule.blockHumanActionsOutsideHours",
																	event.target.checked
																)
															}
														/>
													}
													label={i18n.t("userModal.accessSchedule.blockHumanActionsOutsideHours")}
												/>
											</>
										)}
										{values.userType === "ai_agent" && (

<FormControlLabel
												control={
													<Switch
														color="primary"
														checked={values.accessSchedule?.pauseAiOutsideHours !== false}
														onChange={event =>
															setFieldValue("accessSchedule.pauseAiOutsideHours", event.target.checked)
														}
													/>
												}
												label={i18n.t("userModal.accessSchedule.pauseAiOutsideHours")}
											/>
										)}
										<Typography variant="subtitle2" className={classes.sectionTitle}>
											{i18n.t("userModal.accessSchedule.temporaryOverrideTitle")}
										</Typography>
										<Typography variant="body2" className={classes.sectionHelp}>
											{i18n.t("userModal.accessSchedule.temporaryOverrideHelp")}
										</Typography>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.temporaryOverrideUntil")}
											name="accessSchedule.temporaryOverrideUntil"
											type="datetime-local"
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextField}
											label={i18n.t("userModal.accessSchedule.temporaryOverrideReason")}
											name="accessSchedule.temporaryOverrideReason"
											variant="outlined"
											margin="dense"
											fullWidth
										/>
											</>
										)}
									</div>
								</div>
									{values.userType === "ai_agent" && (
									<div className={classes.formSection} style={{ order: sectionOrder.aiAgent }}>
										<div className={classes.aiSection}>
											<Typography variant="subtitle2" className={classes.sectionTitle}>
												{i18n.t("userModal.aiAgent.title")}
											</Typography>
										<Typography variant="body2" className={classes.sectionHelp}>
											{i18n.t("userModal.aiAgent.help")}
										</Typography>
										{(userId || values.aiAgentProfile?.metadata?.setupMode === "advanced") && (
										<>
											{values.aiAgentProfile?.metadata?.setupMode === "advanced" && (
											<Button
												variant="outlined"
												color="primary"
												className={classes.actionButton}
												onClick={() => changeAiSetupMode("guided")}
											>
												{i18n.t("userModal.aiAgent.wizard.guidedMode")}
											</Button>
											)}
										<div className={classes.wizardFrame}><Typography variant="subtitle2">{i18n.t("userModal.configuration.statusTitle")}: {i18n.t("userModal.configuration." + (user.aiAgentProfile?.lastSyncError ? "statusPending" : user.aiAgentProfile?.status === "active" && user.aiAgentProfile?.readinessStatus === "ready" ? "statusReady" : user.aiAgentProfile?.status === "draft" ? "statusDraft" : "statusPending"))}</Typography><Typography variant="body2" color="textSecondary">{i18n.t("userModal.configuration.statusHelp")}</Typography></div>
<FormControlLabel
												control={
													<Switch
														color="primary"
														checked={values.aiSignMessages !== false}
														onChange={event =>
															setFieldValue("aiSignMessages", event.target.checked)
														}
													/>
												}
											label={i18n.t("userModal.aiAgent.signMessages")}
										/>

											<Field
												as={TextField}
												label={i18n.t("userModal.aiAgent.description")}
												name="aiAgentProfile.description"
												variant="outlined"
												margin="dense"
												fullWidth
											/>
											<Accordion
												className={classes.promptAccordion}
												classes={{ expanded: classes.expanded }}
											>
												<AccordionSummary
													expandIcon={<ExpandMore />}
													className={classes.promptSummary}
													classes={{ expanded: classes.expanded }}
												>
													<Typography variant="subtitle2">
														{i18n.t("userModal.aiAgent.instructions")}
													</Typography>
													<Typography variant="caption" className={classes.promptSummaryText}>
														{i18n.t("userModal.aiAgent.help")}
													</Typography>
												</AccordionSummary>
												<AccordionDetails className={classes.promptDetails}>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.instructions")}
														name="aiAgentProfile.instructions"
														error={Boolean(touched.aiAgentProfile?.instructions && errors.aiAgentProfile?.instructions)}
														helperText={touched.aiAgentProfile?.instructions && errors.aiAgentProfile?.instructions}
														variant="outlined"
														margin="dense"
														multiline
														minRows={6}
														fullWidth
													/>
												</AccordionDetails>
											</Accordion>
											<AiOperationalPolicy values={values} setFieldValue={setFieldValue} userId={userId} />
                        <AiOperationalSkills
							values={values}
							setFieldValue={setFieldValue}
						/>
<Accordion className={classes.promptAccordion}>
<AccordionSummary expandIcon={<ExpandMore />}><div><Typography variant="subtitle2">{i18n.t("userModal.configuration.advancedTitle")}</Typography><Typography variant="body2" color="textSecondary">{i18n.t("userModal.configuration.advancedHelp")}</Typography></div></AccordionSummary>
<AccordionDetails className={classes.promptDetails}>
<div className={classes.compactGrid}>
												<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.llmProvider")}
													name="aiAgentProfile.llmProvider"
													error={Boolean(touched.aiAgentProfile?.llmProvider && errors.aiAgentProfile?.llmProvider)}
													helperText={touched.aiAgentProfile?.llmProvider && errors.aiAgentProfile?.llmProvider}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
												<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.llmModel")}
													name="aiAgentProfile.llmModel"
													error={Boolean(touched.aiAgentProfile?.llmModel && errors.aiAgentProfile?.llmModel)}
													helperText={touched.aiAgentProfile?.llmModel && errors.aiAgentProfile?.llmModel}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
												<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.temperature")}
													name="aiAgentProfile.temperature"
													type="number"
													inputProps={{ min: 0, max: 2, step: 0.1 }}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
												<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.maxTokens")}
													name="aiAgentProfile.maxTokens"
													type="number"
													inputProps={{ min: 1, step: 1 }}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</div>
</AccordionDetails></Accordion>
											<Typography variant="subtitle2" className={classes.sectionTitle}>
												{i18n.t("userModal.aiAgent.mediaTitle")}
											</Typography>
											<Typography variant="body2" className={classes.sectionHelp}>
												{i18n.t("userModal.aiAgent.mediaHelp")}
											</Typography>
											<div className={classes.switchGrid}>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={values.aiAgentProfile?.allowAudioMessages === true}
															onChange={event =>
																setFieldValue(
																	"aiAgentProfile.allowAudioMessages",
																	event.target.checked
																)
															}
														/>
													}
													label={i18n.t("userModal.aiAgent.allowAudioMessages")}
												/>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={values.aiAgentProfile?.allowImageMessages === true}
															onChange={event =>
																setFieldValue(
																	"aiAgentProfile.allowImageMessages",
																	event.target.checked
																)
															}
														/>
													}
													label={i18n.t("userModal.aiAgent.allowImageMessages")}
												/>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={values.aiAgentProfile?.allowDocumentMessages === true}
															onChange={event =>
																setFieldValue(
																	"aiAgentProfile.allowDocumentMessages",
																	event.target.checked
																)
															}
														/>
													}
													label={i18n.t("userModal.aiAgent.allowDocumentMessages")}
												/>
											</div>
											<Field
												as={TextField}
												label={i18n.t("userModal.aiAgent.maxMediaFileSizeMb")}
												name="aiAgentProfile.maxMediaFileSizeMb"
												type="number"
												inputProps={{ min: 1, max: 100, step: 1 }}
												variant="outlined"
												margin="dense"
												fullWidth
											/>
											<div className={classes.compactGrid}>
												<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.responseDelaySeconds")}
													name="aiAgentProfile.responseDelaySeconds"
													type="number"
													inputProps={{ min: 0, max: 120, step: 1 }}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
												{getConfigurationMode(values.aiAgentProfile) !== "operational" && (
<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.proactiveDelayMinutes")}
													name="aiAgentProfile.proactiveDelayMinutes"
													type="number"
													inputProps={{ min: 0, step: 1 }}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
)}
												{getConfigurationMode(values.aiAgentProfile) !== "operational" && (
<Field
													as={TextField}
													label={i18n.t("userModal.aiAgent.maxProactiveMessages")}
													name="aiAgentProfile.maxProactiveMessages"
													type="number"
													inputProps={{ min: 0, step: 1 }}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
)}
											</div>
											<Field
												as={TextField}
												label={i18n.t("userModal.aiAgent.unavailableMessage")}
												name="aiAgentProfile.unavailableMessage"
												variant="outlined"
												margin="dense"
												multiline
												minRows={3}
												helperText={i18n.t("userModal.aiAgent.unavailableMessageHelp")}
												fullWidth
											/>
											{(getConfigurationMode(values.aiAgentProfile) === "operational" || values.aiAgentProfile?.metadata?.operationalFailurePolicy) && <AiOperationalFailureSettings
                                              profile={values.aiAgentProfile} queues={queues} setFieldValue={setFieldValue} />}
											{getConfigurationMode(values.aiAgentProfile) !== "operational" && <>
<Typography variant="subtitle2">{i18n.t("userModal.configuration.legacyTitle")}</Typography>
<Typography variant="body2" color="textSecondary">{i18n.t("userModal.configuration.legacyHelp")}</Typography>
<Accordion
												className={classes.promptAccordion}
												classes={{ expanded: classes.expanded }}
											>
												<AccordionSummary
													expandIcon={<ExpandMore />}
													className={classes.promptSummary}
													classes={{ expanded: classes.expanded }}
												>
													<Typography variant="subtitle2">
														{i18n.t("userModal.aiAgent.proactivePrompt")}
													</Typography>
													<Typography variant="caption" className={classes.promptSummaryText}>
														{i18n.t("userModal.aiAgent.proactiveDelayMinutes")} / {i18n.t("userModal.aiAgent.maxProactiveMessages")}
													</Typography>
												</AccordionSummary>
												<AccordionDetails className={classes.promptDetails}>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.proactivePrompt")}
														name="aiAgentProfile.proactivePrompt"
														variant="outlined"
														margin="dense"
														multiline
														minRows={5}
														fullWidth
													/>
												</AccordionDetails>
											</Accordion>
											<Accordion
												className={classes.promptAccordion}
												classes={{ expanded: classes.expanded }}
											>
												<AccordionSummary
													expandIcon={<ExpandMore />}
													className={classes.promptSummary}
													classes={{ expanded: classes.expanded }}
												>
													<Typography variant="subtitle2">
														{i18n.t("userModal.aiAgent.proactiveClosurePrompt")}
													</Typography>
													<Typography variant="caption" className={classes.promptSummaryText}>
														{i18n.t("userModal.aiAgent.proactiveClosurePromptHelp")}
													</Typography>
												</AccordionSummary>
												<AccordionDetails className={classes.promptDetails}>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.proactiveClosurePrompt")}
														name="aiAgentProfile.proactiveClosurePrompt"
														variant="outlined"
														margin="dense"
														multiline
														minRows={5}
														helperText={i18n.t("userModal.aiAgent.proactiveClosurePromptHelp")}
														fullWidth
													/>
												</AccordionDetails>
											</Accordion>
											<FormControlLabel
												control={
													<Switch
														color="primary"
														checked={values.aiAgentProfile?.proactiveCloseSilently !== false}
														onChange={event =>
															setFieldValue(
																"aiAgentProfile.proactiveCloseSilently",
																event.target.checked
															)
														}
														/>
													}
													label={i18n.t("userModal.aiAgent.proactiveCloseSilently")}
												/>
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={Boolean(values.aiAgentProfile?.allowDirectTicketClosure)}
															onChange={event =>
																setFieldValue(
																	"aiAgentProfile.allowDirectTicketClosure",
																	event.target.checked
																)
															}
														/>
													}
													label={i18n.t("userModal.aiAgent.allowDirectTicketClosure")}
												/>
												<Typography variant="caption" color="textSecondary" display="block">
													{i18n.t("userModal.aiAgent.allowDirectTicketClosureHelp")}
												</Typography>
</>}
							<FormControl
								variant="outlined"
								className={classes.formControl}
								margin="dense"
							>
								<InputLabel id="ai-loop-handoff-queue-label">
									{i18n.t("userModal.aiAgent.loopHandoffQueue")}
								</InputLabel>
								<Field
									as={Select}
									label={i18n.t("userModal.aiAgent.loopHandoffQueue")}
									name="aiAgentProfile.loopHandoffQueueId"
									labelId="ai-loop-handoff-queue-label"
									id="ai-loop-handoff-queue"
								>
									<MenuItem value="">
										{i18n.t("userModal.aiAgent.loopHandoffQueueNone")}
									</MenuItem>
									{queues.map(queue => (
										<MenuItem key={queue.id} value={queue.id}>
											{queue.name}
										</MenuItem>
									))}
								</Field>
							</FormControl>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={Boolean(values.aiAgentProfile?.dissatisfactionEscalationEnabled)}
										onChange={event =>
											setFieldValue(
												"aiAgentProfile.dissatisfactionEscalationEnabled",
												event.target.checked
											)
										}
									/>
								}
								label={i18n.t("userModal.aiAgent.dissatisfactionEscalation")}
							/>
							{values.aiAgentProfile?.dissatisfactionEscalationEnabled && (
								<FormControl
									variant="outlined"
									className={classes.formControl}
									margin="dense"
									fullWidth
								>
									<InputLabel id="ai-dissatisfaction-queue-label">
										{i18n.t("userModal.aiAgent.dissatisfactionEscalationQueue")}
									</InputLabel>
									<Field
										as={Select}
										label={i18n.t("userModal.aiAgent.dissatisfactionEscalationQueue")}
										name="aiAgentProfile.dissatisfactionEscalationQueueId"
										labelId="ai-dissatisfaction-queue-label"
										id="ai-dissatisfaction-queue"
									>
										<MenuItem value="">
											{i18n.t("userModal.aiAgent.dissatisfactionEscalationQueueNone")}
										</MenuItem>
										{queues.map(queue => (
											<MenuItem key={queue.id} value={queue.id}>
												{queue.name}
											</MenuItem>
										))}
									</Field>
									<Typography variant="caption" color="textSecondary">
										{i18n.t("userModal.aiAgent.dissatisfactionEscalationHelp")}
									</Typography>
								</FormControl>
							)}
							<Accordion className={classes.promptAccordion}><AccordionSummary expandIcon={<ExpandMore />}><div><Typography variant="subtitle2">{i18n.t("userModal.configuration.knowledgeTitle")}</Typography><Typography variant="body2" color="textSecondary">{i18n.t("userModal.configuration.knowledgeHelp")}</Typography></div></AccordionSummary><AccordionDetails className={classes.promptDetails}><div className={classes.capabilitiesHeader}>
												<div>
													<Typography variant="subtitle2" className={classes.sectionTitle}>
														{i18n.t("userModal.configuration.knowledgeTitle")}
													</Typography>
													<Typography variant="body2" className={classes.sectionHelp}>
														{i18n.t("userModal.configuration.knowledgeHelp")}
													</Typography>
												</div>
												<Tooltip title={i18n.t("userModal.aiAgent.addCapability")}>
													<IconButton
														color="primary"
														size="small"
														onClick={() => {
															const capabilities = values.aiAgentProfile?.capabilities || [];
															setFieldValue("aiAgentProfile.capabilities", [
																...capabilities,
																{
																	name: "",
																	description: "",
																	type: "custom",
																	instructions: "",
																	enabled: true,
																	metadata: {}
																}
															]);
														}}
													>
														<AddCircleOutline />
													</IconButton>
												</Tooltip>
											</div>
											{(values.aiAgentProfile?.capabilities || []).map((capability, index) => (
												<div className={classes.capabilityItem} key={capability.id || index}>
													<div className={classes.capabilityTitleRow}>
														<FormControlLabel
															control={
																<Switch
																	color="primary"
																	checked={capability.enabled !== false}
																	onChange={event =>
																		setFieldValue(
																			`aiAgentProfile.capabilities.${index}.enabled`,
																			event.target.checked
																		)
																	}
																/>
															}
															label={i18n.t("userModal.aiAgent.capabilityEnabled")}
														/>
														<Tooltip title={i18n.t("userModal.aiAgent.removeCapability")}>
															<IconButton
																size="small"
																onClick={() => {
																	const capabilities = values.aiAgentProfile?.capabilities || [];
																	setFieldValue(
																		"aiAgentProfile.capabilities",
																		capabilities.filter((_, itemIndex) => itemIndex !== index)
																	);
																}}
															>
																<DeleteOutline />
															</IconButton>
														</Tooltip>
													</div>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.capabilityName")}
														name={`aiAgentProfile.capabilities.${index}.name`}
														error={Boolean(touched.aiAgentProfile?.capabilities?.[index]?.name && errors.aiAgentProfile?.capabilities?.[index]?.name)}
														helperText={touched.aiAgentProfile?.capabilities?.[index]?.name && errors.aiAgentProfile?.capabilities?.[index]?.name}
														variant="outlined"
														margin="dense"
														fullWidth
													/>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.capabilityType")}
														name={`aiAgentProfile.capabilities.${index}.type`}
														error={Boolean(touched.aiAgentProfile?.capabilities?.[index]?.type && errors.aiAgentProfile?.capabilities?.[index]?.type)}
														helperText={touched.aiAgentProfile?.capabilities?.[index]?.type && errors.aiAgentProfile?.capabilities?.[index]?.type}
														variant="outlined"
														margin="dense"
														fullWidth
													/>
													<Field
														as={TextField}
														label={i18n.t("userModal.aiAgent.capabilityDescription")}
														name={`aiAgentProfile.capabilities.${index}.description`}
														error={Boolean(touched.aiAgentProfile?.capabilities?.[index]?.description && errors.aiAgentProfile?.capabilities?.[index]?.description)}
														helperText={touched.aiAgentProfile?.capabilities?.[index]?.description && errors.aiAgentProfile?.capabilities?.[index]?.description}
														variant="outlined"
														margin="dense"
														fullWidth
													/>
													<Accordion
														className={classes.promptAccordion}
														classes={{ expanded: classes.expanded }}
													>
														<AccordionSummary
															expandIcon={<ExpandMore />}
															className={classes.promptSummary}
															classes={{ expanded: classes.expanded }}
														>
															<Typography variant="subtitle2">
																{i18n.t("userModal.aiAgent.capabilityInstructions")}
															</Typography>
															<Typography variant="caption" className={classes.promptSummaryText}>
																{capability.description || i18n.t("userModal.aiAgent.capabilityDescription")}
															</Typography>
														</AccordionSummary>
														<AccordionDetails className={classes.promptDetails}>
															<Field
																as={TextField}
																label={i18n.t("userModal.aiAgent.capabilityInstructions")}
																name={`aiAgentProfile.capabilities.${index}.instructions`}
																error={Boolean(touched.aiAgentProfile?.capabilities?.[index]?.instructions && errors.aiAgentProfile?.capabilities?.[index]?.instructions)}
																helperText={touched.aiAgentProfile?.capabilities?.[index]?.instructions && errors.aiAgentProfile?.capabilities?.[index]?.instructions}
																variant="outlined"
																margin="dense"
																multiline
																minRows={4}
																fullWidth
															/>
														</AccordionDetails>
													</Accordion>
												</div>
											))}</AccordionDetails></Accordion>
										</>
										)}
									</div>
								</div>
								)}
								</>
								)}
								</>
								)}
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSaving}
									variant="outlined"
									className={classes.actionButton}
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<div className={classes.actionsSpacer} />
								{!showCreationTypeChoice && (
									<>
									{useSimplifiedWizard ? (
										<>
											{creationStep > 0 && (
												<Button
													type="button"
													className={classes.actionButton}
													disabled={isSaving}
													onClick={() => {
														if (isAiWizard) setFieldValue("aiAgentProfile.metadata.wizardCompleted", false, false);
														setCreationStep(step => Math.max(step - 1, 0));
													}}
												>
													{i18n.t("userModal.aiAgent.wizard.back")}
												</Button>
											)}
											{isAiWizard && (
												<Button
													type="submit"
													color="primary"
													variant="outlined"
													className={classes.actionButton}
													disabled={isSaving || String(values.name || "").trim().length < 2}
													onClick={() => { deploymentActionRef.current = "save_draft"; }}
												>
													{i18n.t("userModal.aiAgent.wizard.saveDraft")}
												</Button>
											)}
											{!isWizardFinalStep ? (
												<Button
													type="button"
													color="primary"
													variant="contained"
													disableElevation
													className={classes.actionButton}
													disabled={isSaving}
													onClick={goToNextCreationStep}
												>
													{i18n.t("userModal.aiAgent.wizard.next")}
												</Button>
											) : (
												<ButtonWithSpinner
													type="submit"
													color="primary"
													loading={isSaving}
													variant="contained"
													className={classes.actionButton}
													onClick={() => { deploymentActionRef.current = "publish"; }}
													disabled={isSaving || (isHumanWizard ? !humanReadiness.isReady : !aiReadiness.isReady)}
												>
													{isHumanWizard
														? i18n.t("userModal.humanWizard.create")
														: i18n.t("userModal.aiAgent.wizard.publish")}
												</ButtonWithSpinner>
											)}
										</>
									) : (
									<>
									{values.userType === "ai_agent" &&
									values.aiAgentProfile?.metadata?.setupMode === "guided" &&
									(!userId || values.aiAgentProfile?.readinessStatus !== "ready") && (
									<Button
										type="submit"
										color="primary"
										variant="outlined"
										className={classes.actionButton}
										disabled={isSaving}
										onClick={() => { deploymentActionRef.current = "save_draft"; }}
									>
										{i18n.t("userModal.aiAgent.wizard.saveDraft")}
									</Button>
								)}
								<ButtonWithSpinner
									type="submit"
									color="primary"
									loading={isSaving}
									variant="contained"
									className={classes.actionButton}
									onClick={() => { deploymentActionRef.current = "publish"; }}
									disabled={
										isSaving ||
										(!userId && values.userType === "ai_agent" &&
											values.aiAgentProfile?.metadata?.setupMode === "guided" &&
											values.aiAgentProfile?.metadata?.wizardCompleted !== true)
									}
								>
									{isSaving
										? i18n.t("userModal.buttons.saving")
										: values.userType === "ai_agent" && values.aiAgentProfile?.metadata?.setupMode === "guided"
										? i18n.t("userModal.aiAgent.wizard.publish")
										: userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
								</ButtonWithSpinner>
									</>
									)}
									</>
								)}
							</DialogActions>
						</Form>
					)}}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
